/**
 * Collapsed durable work trail under a completed assistant message.
 * Shows step/tool summary; expands to the same timeline used while thinking.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, ListChecks, Wrench } from 'lucide-react';
import { cn } from '@flowstate/utils';
import type { OrchestratorMeta, ToolActivity } from '../types/chat';
import { ToolActivityTimeline } from './ToolActivityTimeline';

interface WorkTrailProps {
  thinkingSteps?: string[];
  currentStep?: number;
  toolActivity?: ToolActivity;
  modelName?: string;
  className?: string;
}

function hasTrailContent(
  thinkingSteps?: string[],
  toolActivity?: ToolActivity,
  modelName?: string,
): boolean {
  const stepCount = thinkingSteps?.length ?? 0;
  const toolCount = toolActivity?.entries?.length ?? 0;
  return stepCount > 0 || toolCount > 0 || Boolean(modelName);
}

export function workTrailFromMeta(meta?: OrchestratorMeta): WorkTrailProps | null {
  if (!meta) return null;
  const thinkingSteps = meta.thinkingSteps;
  const toolActivity = meta.toolActivity;
  const modelName = meta.modelName;
  if (!hasTrailContent(thinkingSteps, toolActivity, modelName)) return null;
  return {
    thinkingSteps,
    currentStep: meta.currentStep,
    toolActivity,
    modelName,
  };
}

export const WorkTrail: React.FC<WorkTrailProps> = ({
  thinkingSteps,
  currentStep,
  toolActivity,
  modelName,
  className,
}) => {
  const [open, setOpen] = useState(false);

  if (!hasTrailContent(thinkingSteps, toolActivity, modelName)) return null;

  const stepCount = thinkingSteps?.length ?? 0;
  const toolCount = toolActivity?.entries?.length ?? 0;
  const summaryParts: string[] = [];
  if (modelName) {
    summaryParts.push(modelName);
  }
  if (stepCount > 0) {
    summaryParts.push(`${stepCount} step${stepCount === 1 ? '' : 's'}`);
  }
  if (toolCount > 0) {
    summaryParts.push(`${toolCount} tool${toolCount === 1 ? '' : 's'}`);
  }

  return (
    <div className={cn('mt-3 pt-3 border-t border-border/30', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left',
          'text-xs text-muted-foreground hover:bg-muted/40 hover:text-foreground',
          'transition-colors duration-150',
        )}
        aria-expanded={open}
      >
        <ListChecks className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="flex-1 font-medium">{summaryParts.join(' · ')}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.15 }}
          className="flex-shrink-0"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 space-y-2 px-1">
              {thinkingSteps && thinkingSteps.length > 0 && (
                <ul className="space-y-1">
                  {thinkingSteps.map((label, idx) => {
                    const done =
                      typeof currentStep === 'number' ? idx <= currentStep : true;
                    return (
                      <li
                        key={`${label}-${idx}`}
                        className={cn(
                          'flex items-center gap-2 text-xs px-2 py-1 rounded-md',
                          done ? 'text-foreground' : 'text-muted-foreground',
                        )}
                      >
                        <span
                          className={cn(
                            'h-1.5 w-1.5 rounded-full flex-shrink-0',
                            done ? 'bg-primary' : 'bg-muted-foreground/40',
                          )}
                        />
                        <span>{label}</span>
                      </li>
                    );
                  })}
                </ul>
              )}

              {toolActivity && toolActivity.entries.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-2 mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <Wrench className="h-3 w-3" />
                    Tools
                  </div>
                  <ToolActivityTimeline activity={toolActivity} compact />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WorkTrail;
